import 'whatwg-fetch';
import Auth from '../../Auth';
import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, gql } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { useLazyQuery } from '@apollo/client/react';

export interface SessionCreateResponse {
  id_token: string;
}

const API_BASE = process.env.REACT_APP_API || '';

const httpLink = new HttpLink({ uri: `${API_BASE}/api/graphql` });

const authLink = new SetContextLink((prevContext, _operation) => {
  const token = Auth.getToken();
  return {
    headers: {
      ...prevContext.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

export const client = new ApolloClient({
  link: ApolloLink.from([authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: { fetchPolicy: 'no-cache' },
    watchQuery: { fetchPolicy: 'no-cache' },
  },
});

export class SessionApi {
  static login(username: string, password: string) {
    return fetch(`${API_BASE}/authenticate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  }

  static async changeClient(clientId: number): Promise<SessionCreateResponse> {
    const result = await client.query<{ changeClient: SessionCreateResponse }>({
      query: gql`
        query changeClient($clientId: Int!) {
          changeClient(clientId: $clientId) {
            id_token
          }
        }
      `,
      variables: { clientId },
    });
    return result.data.changeClient;
  }

  static async changeAddon(addonId: number): Promise<SessionCreateResponse> {
    const result = await client.query<{ changeAddon: SessionCreateResponse }>({
      query: gql`
        query changeAddon($addonId: Int!) {
          changeAddon(addonId: $addonId) {
            id_token
          }
        }
      `,
      variables: { addonId },
    });
    return result.data.changeAddon;
  }
}

export const useChangeClient = () => {
  return useLazyQuery<{ changeClient: SessionCreateResponse }>(
    gql`
      query changeClient($clientId: Int!) {
        changeClient(clientId: $clientId) {
          id_token
        }
      }
    `,
    {
      client,
      fetchPolicy: 'no-cache',
    }
  );
};

export const useChangeAddon = () => {
  return useLazyQuery<{ changeAddon: SessionCreateResponse }>(
    gql`
      query changeAddon($addonId: Int!) {
        changeAddon(addonId: $addonId) {
          id_token
        }
      }
    `,
    {
      client,
      fetchPolicy: 'no-cache',
    }
  );
};
